import PropTypes from 'prop-types';
import { BsGeoAlt, BsKanban, BsCurrencyEuro } from 'react-icons/bs';

function formatDate(iso) {
	if (!iso) return '–';
	return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function initials(name) {
	if (!name) return '?';
	return name
		.split(/\s+/)
		.slice(0, 2)
		.map((w) => w[0])
		.join('')
		.toUpperCase();
}

function DaysRemaining({ days }) {
	return (
		<span className={`text-sm font-medium tabular-nums ${days <= 7 ? 'text-warning-500' : 'text-bgBase-800'}`}>
			{days} {days === 1 ? 'Tag' : 'Tage'}
		</span>
	);
}

DaysRemaining.propTypes = { days: PropTypes.number.isRequired };

function IconBadge({ className, children }) {
	return (
		<div className={`w-8 h-8 rounded-control flex items-center justify-center flex-shrink-0 ${className}`}>
			{children}
		</div>
	);
}

IconBadge.propTypes = { className: PropTypes.string.isRequired, children: PropTypes.node };

function NameCell({ badge, name }) {
	return (
		<div className="flex items-center gap-3 min-w-0">
			{badge}
			<span className="font-medium text-text truncate">{name}</span>
		</div>
	);
}

NameCell.propTypes = { badge: PropTypes.node, name: PropTypes.node };

const deletedAtColumn = {
	key: 'deletedAt',
	label: 'Gelöscht am',
	width: 140,
	render: (row) => formatDate(row.deletedAt),
};

const daysRemainingColumn = {
	key: 'daysRemaining',
	label: 'Verbleibend',
	align: 'right',
	width: 110,
	render: (row) => <DaysRemaining days={row.daysRemaining} />,
};

export const COMPANY_COLUMNS = [
	{
		key: 'companyName',
		label: 'Unternehmen',
		render: (c) => (
			<NameCell
				badge={
					<IconBadge className="bg-gradient-to-br from-bgBase-500 to-bgBase-600 text-bgBase-800 text-xs font-bold">
						{initials(c.companyName)}
					</IconBadge>
				}
				name={c.companyName}
			/>
		),
	},
	{
		key: 'addressCity',
		label: 'Standort',
		render: (c) =>
			c.addressCity ? (
				<span className="flex items-center gap-1 truncate text-bgBase-800">
					<BsGeoAlt className="w-3 h-3 flex-shrink-0" />
					{c.addressCity}
				</span>
			) : (
				<span className="text-bgBase-800">{'–'}</span>
			),
	},
	deletedAtColumn,
	daysRemainingColumn,
];

export const DEAL_COLUMNS = [
	{
		key: 'title',
		label: 'Deal',
		render: (d) => (
			<NameCell
				badge={
					<IconBadge className="bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700">
						<BsCurrencyEuro className="w-3.5 h-3.5" />
					</IconBadge>
				}
				name={d.title}
			/>
		),
	},
	{
		key: 'companyNameSnapshot',
		label: 'Unternehmen',
		render: (d) => <span className="truncate text-bgBase-800">{d.companyNameSnapshot || '–'}</span>,
	},
	{
		key: 'dealValue',
		label: 'Wert',
		width: 120,
		render: (d) => (
			<span className="tabular-nums text-bgBase-800">
				{d.dealValue !== null ? `${Number(d.dealValue).toLocaleString('de-DE')} €` : '–'}
			</span>
		),
	},
	deletedAtColumn,
	daysRemainingColumn,
];

export const PIPELINE_COLUMNS = [
	{
		key: 'name',
		label: 'Pipeline',
		render: (p) => (
			<NameCell
				badge={
					<IconBadge className="bg-gradient-to-br from-primary-100 to-primary-200 text-primary-600">
						<BsKanban className="w-3.5 h-3.5" />
					</IconBadge>
				}
				name={p.name}
			/>
		),
	},
	{
		key: 'description',
		label: 'Beschreibung',
		render: (p) => <span className="truncate text-bgBase-800">{p.description || '–'}</span>,
	},
	deletedAtColumn,
	daysRemainingColumn,
];
